
import { useCallback, useSyncExternalStore } from 'react';
import { useLanguage } from '@/components/LanguageContext';

// Pre-load ALL translation files at build time using Vite's glob import (eager)
const allTranslations: Record<string, Record<string, any>> = {};

const translationModules = import.meta.glob('../translations/**/*.json', { eager: true }) as Record<string, { default: any }>;

// Parse the glob results into a structured cache: { "namespace/language": data }
for (const [path, module] of Object.entries(translationModules)) {
  // path looks like: ../translations/schedule/pt.json
  const match = path.match(/\.\.\/translations\/(.+)\/(\w+)\.json$/);
  if (match) {
    const [, namespace, lang] = match;
    const key = `${namespace}/${lang}`;
    allTranslations[key] = module.default || module;
  }
}

// Also handle root-level translations (pt.json, en.json, etc.)
for (const [path, module] of Object.entries(translationModules)) {
  const match = path.match(/\.\.\/translations\/(\w+)\.json$/);
  if (match) {
    const [, lang] = match;
    const key = `_root/${lang}`;
    allTranslations[key] = module.default || module;
  }
}

export const useTranslations = (namespace: string) => {
  const { language } = useLanguage();

  const translations = allTranslations[`${namespace}/${language}`] || {};

  const t = useCallback((key: string, fallback?: string): string => {
    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (!value || value[k] === undefined) {
        return fallback || '';
      }
      value = value[k];
    }

    return typeof value === 'string' ? value : (fallback || '');
  }, [translations]);

  return { t, loading: false };
};
